import { createSlice, nanoid } from "@reduxjs/toolkit";
import { streamMessage } from "./chat.service";

// Message shapes:
//   user      → { id, role:'user', content }
//   assistant → { id, role:'assistant', text, status, streaming, isError }
const initialState = {
  isOpen: false,
  messages: [],
  isLoading: false,
  error: null,
};

const chatSlice = createSlice({
  name: "chat",
  initialState,
  reducers: {
    openWidget: (state) => { state.isOpen = true; },
    closeWidget: (state) => { state.isOpen = false; },
    toggleWidget: (state) => { state.isOpen = !state.isOpen; },
    clearChat: (state) => { state.messages = []; state.error = null; },

    addUserMessage: (state, action) => {
      state.messages.push({ id: nanoid(), role: "user", content: action.payload });
    },
    beginAssistant: (state, action) => {
      state.isLoading = true;
      state.error = null;
      state.messages.push({
        id: action.payload,
        role: "assistant",
        text: "",
        status: "Thinking…",
        streaming: true,
      });
    },
    setAssistantStatus: (state, action) => {
      const m = state.messages.find((x) => x.id === action.payload.id);
      if (m && m.streaming) m.status = action.payload.text;
    },
    appendAssistantDelta: (state, action) => {
      const m = state.messages.find((x) => x.id === action.payload.id);
      if (m) { m.text += action.payload.text; m.status = null; }
    },
    endAssistant: (state, action) => {
      const m = state.messages.find((x) => x.id === action.payload.id);
      if (m) { m.streaming = false; m.status = null; }
      state.isLoading = false;
    },
    failAssistant: (state, action) => {
      const m = state.messages.find((x) => x.id === action.payload.id);
      if (m) {
        m.streaming = false;
        m.status = null;
        m.isError = true;
        if (!m.text) m.text = action.payload.message;
        else m.text += `\n\n_${action.payload.message}_`;
      }
      state.isLoading = false;
      state.error = action.payload.message;
    },
  },
});

export const {
  openWidget, closeWidget, toggleWidget, clearChat,
  addUserMessage, beginAssistant, setAssistantStatus,
  appendAssistantDelta, endAssistant, failAssistant,
} = chatSlice.actions;

/**
 * Thunk: send a user message and stream the assistant's reply.
 * Dispatches granular actions as status/delta events arrive.
 */
export const sendMessage = (text) => async (dispatch, getState) => {
  const trimmed = (text || "").trim();
  if (!trimmed) return;

  // Build text-only history from prior turns BEFORE adding the new message.
  const prior = getState().chat.messages.map((m) =>
    m.role === "assistant"
      ? { role: "assistant", content: m.text || "" }
      : { role: "user", content: m.content }
  );
  const wire = [...prior, { role: "user", content: trimmed }];

  const assistantId = nanoid();
  dispatch(addUserMessage(trimmed));
  dispatch(beginAssistant(assistantId));

  try {
    await streamMessage(wire, {
      onStatus: (s) => dispatch(setAssistantStatus({ id: assistantId, text: s })),
      onDelta: (d) => dispatch(appendAssistantDelta({ id: assistantId, text: d })),
    });
    dispatch(endAssistant(assistantId));
  } catch (e) {
    dispatch(failAssistant({ id: assistantId, message: e.message || "The assistant is unavailable right now." }));
  }
};

export default chatSlice.reducer;
